import type { ComponentType } from 'react';

import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { HeadingProps } from '@atlaskit/heading';
import type { IconTileProps, NewCoreIconProps } from '@atlaskit/icon';

import { EMPTY_CONVERSATION_ID } from '../chat';

export type Menu =
	| 'conversation-list'
	| 'agents'
	| 'pulse'
	| 'tasks'
	| 'scheduled'
	| 'recommended-spaces'
	| 'browse-spaces'
	| 'space-landing'
	| 'none';

/**
 * A minimal space shape stored in ui-store. Structurally compatible with the full
 * `Space` type from `@atlassian/rovo-spaces/controllers/space-store/types` but kept separate here
 * to avoid a circular dependency (rovo-navigation already depends on this package).
 *
 * TODO (ROVOFE-2740): Move this to a shared `@atlassian/rovo-spaces-types` package so
 * rovo-spaces, rovo-navigation and conversation-assistant-store all import from one place.
 */
export type SelectedSpaceSource = {
	ari: string;
	url: string;
	sourceType: string;
	name?: string;
};

export type SelectedSpace = {
	id: string;
	name: string;
	emoji?: string | null;
	sources?: SelectedSpaceSource[];
};

export type ConversationHeaderState = {
	conversationId: string;
	icon?: ComponentType<NewCoreIconProps>;
	iconAppearance?: IconTileProps['appearance'];
} | null;

/**
 * A serializable action descriptor stored in the UI store for empty-chat launches.
 * Uses string literals instead of importing from @atlassian/rovo-conversation-actions
 * to avoid a circular dependency:
 *   conversation-assistant-store → rovo-conversation-actions → conversation-assistant-store
 *
 * String values correspond to:
 *   type: AIActionType from @atlassian/rovo-conversation-actions/types
 *   handleType: HandleActionType enum from @atlassian/rovo-conversation-actions/types
 */
export type EmptyChatLaunchAction = {
	id: string;
	/** @see AIActionType in @atlassian/rovo-conversation-actions/types */
	type: 'ai-action' | 'agent-action' | 'convo-starter-action' | 'skill-action';
	/** @see HandleActionType in @atlassian/rovo-conversation-actions/types */
	handleType: 'Base' | 'Chat' | 'Nested' | 'Skill';
	staticPrompt?: string;
	staticMessage?: string;
	title?: string;
	icon?: () => React.JSX.Element;
};

export type EmptyChatLaunchGreeting = {
	iconSrc?: string;
	iconSrcDark?: string;
	title?: string;
	titleSize?: HeadingProps['size'];
	subtitle?: string;
	testId?: string;
	animationDuration?: 'small' | 'medium' | 'large';
	entranceDirection?: 'top' | 'bottom' | 'left' | 'right';
	exitDirection?: 'top' | 'bottom' | 'left' | 'right';
	animationKey?: string;
	truncateGreeting?: boolean;
};

/** Project context for empty-chat launches. Mapped to the generic entityContext
 * shape at the display layer (ContextBanner) for entity-agnostic rendering. */
export type EmptyChatLaunchProjectContext = {
	/** Used in the backend sendMessage payload, not for display */
	projectId: string;
	projectName: string;
	projectUrl: string;
	/** When set, passed directly as space_id in sendMessageStream to scope RAG to the space's connected sources. */
	spaceId?: string;
};

type State = {
	activeMenu: Menu;
	mounted: boolean;
	isAgentSelectorMenuOpen: boolean;
	streaming: boolean;
	isChatOnboardingMiniModalOpen: boolean;
	conversationHeader?: ConversationHeaderState;
	emptyChatLaunchGreeting?: EmptyChatLaunchGreeting;
	emptyChatLaunchProjectContext?: EmptyChatLaunchProjectContext;
	emptyChatLaunchConversationActions?: EmptyChatLaunchAction[];
	emptyChatLaunchReplaceProviderActions: boolean;
	emptyChatLaunchSkillRegistryTags?: string[];
	emptyChatLaunchId: number;
	selectedSpace: SelectedSpace | undefined;
	activeTabId: string;
	previousTabId: string;
	lastTabConversationId: string;
	lastLifecycleConversationId: string;
	lastLifecycleLaunchId: number | undefined;
};

const initialState: State = {
	activeMenu: 'none',
	mounted: false,
	isAgentSelectorMenuOpen: false,
	streaming: false,
	isChatOnboardingMiniModalOpen: false, // If productionized will look to move this logic into conversation-assistant-onboarding
	emptyChatLaunchGreeting: undefined,
	emptyChatLaunchProjectContext: undefined,
	emptyChatLaunchConversationActions: undefined,
	emptyChatLaunchReplaceProviderActions: false,
	emptyChatLaunchSkillRegistryTags: undefined,
	emptyChatLaunchId: 0,
	selectedSpace: undefined,
	activeTabId: 'suggestions',
	previousTabId: 'suggestions',
	lastTabConversationId: '',
	lastLifecycleConversationId: EMPTY_CONVERSATION_ID,
	lastLifecycleLaunchId: undefined,
};

const store = createStore({
	name: 'uiStore',
	initialState,
	actions: {
		// created for resetting store during tests
		reset:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					...initialState,
					conversationHeader: null,
					selectedSpace: undefined,
				});
			},
		setActiveMenu:
			(menu: Menu): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ activeMenu: menu });
			},
		setMounted:
			(mounted: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ mounted });
			},
		setIsAgentSelectorMenuOpen:
			(isOpen: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ isAgentSelectorMenuOpen: isOpen });
			},
		setStreamingStatus:
			(status: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ streaming: status });
			},
		setIsChatOnboardingMiniModalOpen:
			(status: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ isChatOnboardingMiniModalOpen: status });
			},
		setConversationHeader:
			(header: ConversationHeaderState): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ conversationHeader: header });
			},
		getIsMounted:
			(): (({ getState }: StoreActionApi<State>) => boolean) =>
			({ getState }): boolean => {
				return getState().mounted;
			},
		setEmptyChatLaunch:
			(payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				/**
				 * When true, customConversationActions fully replace the prop-based getActions function.
				 * This takes precedence over the consumer's prioritizeCustomActionResults prop —
				 * even if prioritizeCustomActionResults=true is set statically, the store actions win
				 * because mergedGetActions returns only the store actions before the display layer is reached.
				 */
				replaceProviderActions?: boolean;

				/**
				 * When provided, these tags are used to fetch skill metadata for the empty state actions.
				 * Only set this when the launch source needs skill hover cards (e.g. rovo-for-you project actions).
				 * If not provided, no additional skill fetch is triggered.
				 */
				skillRegistryTags?: string[];
			}): (({ setState, getState }: StoreActionApi<State>) => void) =>
			({ setState, getState }) => {
				setState({
					emptyChatLaunchGreeting: payload.customGreeting,
					emptyChatLaunchConversationActions: payload.customConversationActions,
					emptyChatLaunchProjectContext: payload.projectContext,
					emptyChatLaunchReplaceProviderActions: payload.replaceProviderActions ?? false,
					emptyChatLaunchSkillRegistryTags: payload.skillRegistryTags,
					emptyChatLaunchId: getState().emptyChatLaunchId + 1,
				});
			},
		clearEmptyChatLaunchGreeting:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					emptyChatLaunchGreeting: undefined,
					emptyChatLaunchConversationActions: undefined,
					emptyChatLaunchReplaceProviderActions: false,
					emptyChatLaunchSkillRegistryTags: undefined,
				});
			},
		clearEmptyChatLaunchAll:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					emptyChatLaunchGreeting: undefined,
					emptyChatLaunchConversationActions: undefined,
					emptyChatLaunchProjectContext: undefined,
					emptyChatLaunchReplaceProviderActions: false,
					emptyChatLaunchSkillRegistryTags: undefined,
				});
			},
		setSelectedSpace:
			(space: SelectedSpace | undefined): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedSpace: space });
			},
		clearSelectedSpace:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ selectedSpace: undefined });
			},
		setActiveTabId:
			(tabId: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ activeTabId: tabId });
			},
		setPreviousTabId:
			(tabId: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ previousTabId: tabId });
			},
		setLastTabConversationId:
			(id: string): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ lastTabConversationId: id });
			},
		checkAndUpdateLifecycleGuard:
			(
				conversationId: string,
				launchId: number | undefined,
			): (({ getState, setState }: StoreActionApi<State>) => boolean) =>
			({ getState, setState }): boolean => {
				const { lastLifecycleConversationId, lastLifecycleLaunchId } = getState();
				if (lastLifecycleConversationId === conversationId && lastLifecycleLaunchId === launchId) {
					return false;
				}
				setState({
					lastLifecycleConversationId: conversationId,
					lastLifecycleLaunchId: launchId,
				});
				return true;
			},
	},
});

const useStore = createHook(store);

export const useEmptyChatLaunchSelector: HookFunction<
	{
		customGreeting: EmptyChatLaunchGreeting | undefined;
		projectContext: EmptyChatLaunchProjectContext | undefined;
		customConversationActions: EmptyChatLaunchAction[] | undefined;
		replaceProviderActions: boolean;
		skillRegistryTags: string[] | undefined;
		launchId: number;
	},
	BoundActions<
		State,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveMenu: (menu: Menu) => ({ setState }: StoreActionApi<State>) => void;
			setMounted: (mounted: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsAgentSelectorMenuOpen: (
				isOpen: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setStreamingStatus: (status: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsChatOnboardingMiniModalOpen: (
				status: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setConversationHeader: (
				header: ConversationHeaderState,
			) => ({ setState }: StoreActionApi<State>) => void;
			getIsMounted: () => ({ getState }: StoreActionApi<State>) => boolean;
			setEmptyChatLaunch: (payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				/**
				 * When true, customConversationActions fully replace the prop-based getActions function.
				 * This takes precedence over the consumer's prioritizeCustomActionResults prop —
				 * even if prioritizeCustomActionResults=true is set statically, the store actions win
				 * because mergedGetActions returns only the store actions before the display layer is reached.
				 */
				replaceProviderActions?: boolean;
				skillRegistryTags?: string[];
			}) => ({ setState, getState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchGreeting: () => ({ setState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchAll: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
			checkAndUpdateLifecycleGuard: (
				conversationId: string,
				launchId: number | undefined,
			) => ({ getState, setState }: StoreActionApi<State>) => boolean;
		}
	>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		customGreeting: state.emptyChatLaunchGreeting,
		projectContext: state.emptyChatLaunchProjectContext,
		customConversationActions: state.emptyChatLaunchConversationActions,
		replaceProviderActions: state.emptyChatLaunchReplaceProviderActions,
		skillRegistryTags: state.emptyChatLaunchSkillRegistryTags,
		launchId: state.emptyChatLaunchId,
	}),
});

export const useActiveTabSelector: HookFunction<
	{
		activeTabId: string;
		previousTabId: string;
		lastTabConversationId: string;
	},
	BoundActions<
		State,
		{
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: State) => ({
		activeTabId: state.activeTabId,
		previousTabId: state.previousTabId,
		lastTabConversationId: state.lastTabConversationId,
	}),
});

export const useUIStore = (): {
	activeMenu: Menu;
	mounted: boolean;
	streaming: boolean;
	isAgentSelectorMenuOpen: boolean;
	conversationHeader?: ConversationHeaderState;
	selectedSpace: SelectedSpace | undefined;
	reset: () => void;
	setActiveMenu: (menu: Menu) => void;
	setMounted: (mounted: boolean) => void;
	setIsAgentSelectorMenuOpen: (isOpen: boolean) => void;
	setStreamingStatus: (status: boolean) => void;
	isChatOnboardingMiniModalOpen: boolean;
	setIsChatOnboardingMiniModalOpen: (status: boolean) => void;
	setConversationHeader: (header: ConversationHeaderState) => void;
	setSelectedSpace: (space: SelectedSpace | undefined) => void;
	clearSelectedSpace: () => void;
	activeTabId: string;
	previousTabId: string;
	lastTabConversationId: string;
	setActiveTabId: (tabId: string) => void;
	setPreviousTabId: (tabId: string) => void;
	setLastTabConversationId: (id: string) => void;
} => {
	const [
		{
			activeMenu,
			mounted,
			streaming,
			isAgentSelectorMenuOpen,
			isChatOnboardingMiniModalOpen,
			conversationHeader,
			selectedSpace,
			activeTabId,
			previousTabId,
			lastTabConversationId,
		},
		{
			reset,
			setActiveMenu,
			setMounted,
			setIsAgentSelectorMenuOpen,
			setStreamingStatus,
			setIsChatOnboardingMiniModalOpen,
			setConversationHeader,
			setSelectedSpace,
			clearSelectedSpace,
			setActiveTabId,
			setPreviousTabId,
			setLastTabConversationId,
		},
	] = useStore();

	return {
		activeMenu,
		mounted,
		streaming,
		isAgentSelectorMenuOpen,
		conversationHeader: conversationHeader ?? null,
		selectedSpace,
		reset,
		setActiveMenu,
		setMounted,
		setIsAgentSelectorMenuOpen,
		setStreamingStatus,
		isChatOnboardingMiniModalOpen,
		setIsChatOnboardingMiniModalOpen,
		setConversationHeader,
		setSelectedSpace,
		clearSelectedSpace,
		activeTabId,
		previousTabId,
		lastTabConversationId,
		setActiveTabId,
		setPreviousTabId,
		setLastTabConversationId,
	};
};

export const useUIStoreActions: HookActionsFunction<
	BoundActions<
		State,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveMenu: (menu: Menu) => ({ setState }: StoreActionApi<State>) => void;
			setMounted: (mounted: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsAgentSelectorMenuOpen: (
				isOpen: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setStreamingStatus: (status: boolean) => ({ setState }: StoreActionApi<State>) => void;
			setIsChatOnboardingMiniModalOpen: (
				status: boolean,
			) => ({ setState }: StoreActionApi<State>) => void;
			setConversationHeader: (
				header: ConversationHeaderState,
			) => ({ setState }: StoreActionApi<State>) => void;
			getIsMounted: () => ({ getState }: StoreActionApi<State>) => boolean;
			setEmptyChatLaunch: (payload: {
				customGreeting?: EmptyChatLaunchGreeting;
				customConversationActions?: EmptyChatLaunchAction[];
				projectContext?: EmptyChatLaunchProjectContext;
				replaceProviderActions?: boolean;
				skillRegistryTags?: string[];
			}) => ({ setState, getState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchGreeting: () => ({ setState }: StoreActionApi<State>) => void;
			clearEmptyChatLaunchAll: () => ({ setState }: StoreActionApi<State>) => void;
			setSelectedSpace: (
				space: SelectedSpace | undefined,
			) => ({ setState }: StoreActionApi<State>) => void;
			clearSelectedSpace: () => ({ setState }: StoreActionApi<State>) => void;
			setActiveTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setPreviousTabId: (tabId: string) => ({ setState }: StoreActionApi<State>) => void;
			setLastTabConversationId: (id: string) => ({ setState }: StoreActionApi<State>) => void;
			checkAndUpdateLifecycleGuard: (
				conversationId: string,
				launchId: number | undefined,
			) => ({ getState, setState }: StoreActionApi<State>) => boolean;
		}
	>
> = createActionsHook(store);
